import "./utils/patchApexCharts"; // Patch antes de qualquer import
import React from "react";
import ReactDOM from "react-dom";
import CssBaseline from "@material-ui/core/CssBaseline";
import * as serviceworker from './serviceWorker'
import ErrorBoundary from "./components/ErrorBoundary";

// Importar CSS do ModalImageCors para garantir que seja carregado
import "./components/ModalImageCors/modal-image-override.css";

import App from "./App";

// ✅ Função para limpar localStorage corrompido na inicialização
const clearCorruptedLocalStorage = () => {
	try {
		// Lista de chaves conhecidas que devem ser JSON
		const jsonKeys = [
			"cshow",
			"sendSignMessage",
			"profileImage",
			"frontendVersion",
			"push-notification-asked"
		];

		// Chaves que são strings simples (não JSON): cores, nome, tema, logos (filename)
		const stringKeys = [
			"token",
			"public-token",
			"preferredTheme",
			"primaryColorLight",
			"primaryColorDark",
			"appName",
			"appLogoLight",
			"appLogoDark",
			"appLogoFavicon"
		];

		let cleanedCount = 0;
		const corruptedKeys = [];

		// Verificar TODAS as chaves do localStorage
		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i);
			if (!key) continue;

			try {
				const item = localStorage.getItem(key);
				if (!item) continue;

				// Se for uma chave que deve ser JSON, tentar parsear
				if (jsonKeys.includes(key)) {
					try {
						JSON.parse(item);
					} catch (e) {
						// Se falhar ao parsear, está corrompido
						console.warn(`⚠️ Chave corrompida detectada: ${key}`);
						corruptedKeys.push(key);
						cleanedCount++;
					}
				}
				// Para outras chaves, verificar se não são strings muito grandes ou inválidas
				else if (!stringKeys.includes(key)) {
					// Tentar detectar se é JSON corrompido (começa com { ou [ mas não é válido)
					if ((item.startsWith('{') || item.startsWith('[')) && item.length > 0) {
						try {
							JSON.parse(item);
						} catch (e) {
							// Se parece JSON mas não é válido, pode estar corrompido
							console.warn(`⚠️ Possível chave corrompida detectada: ${key}`);
							corruptedKeys.push(key);
							cleanedCount++;
						}
					}
				}
			} catch (e) {
				// Erro ao acessar a chave, remover
				console.warn(`⚠️ Erro ao acessar chave ${key}, removendo...`);
				corruptedKeys.push(key);
				cleanedCount++;
			}
		}

		// Remover chaves corrompidas
		corruptedKeys.forEach(key => {
			try {
				localStorage.removeItem(key);
				console.log(`✅ Chave removida: ${key}`);
			} catch (e) {
				console.error(`Erro ao remover chave ${key}:`, e);
			}
		});

		if (cleanedCount > 0) {
			console.log(`✅ Limpeza concluída: ${cleanedCount} chave(s) corrompida(s) removida(s)`);
		}

		// Verificar se o token existe e parece válido (não está corrompido)
		const token = localStorage.getItem("token");
		if (token) {
			try {
				// Token JWT geralmente tem 3 partes separadas por ponto
				const parts = token.split('.');
				if (parts.length !== 3) {
					console.warn("⚠️ Token parece inválido (formato incorreto). Removendo...");
					localStorage.removeItem("token");
					cleanedCount++;
				}
			} catch (e) {
				console.warn("⚠️ Erro ao validar token. Removendo...");
				localStorage.removeItem("token");
				cleanedCount++;
			}
		}

		return cleanedCount;
	} catch (e) {
		console.error("Erro ao limpar localStorage corrompido:", e);
		// Se houver erro crítico, tentar limpar tudo exceto o token
		try {
			const token = localStorage.getItem("token");
			localStorage.clear();
			if (token) {
				try {
					localStorage.setItem("token", token);
				} catch (e2) {
					console.error("Erro ao restaurar token:", e2);
				}
			}
			console.log("✅ localStorage limpo completamente (exceto token)");
		} catch (e2) {
			console.error("Erro crítico ao limpar localStorage:", e2);
		}
		return 0;
	}
};

// ✅ Limpar dados corrompidos antes de renderizar
const cleanedCount = clearCorruptedLocalStorage();

// ✅ Se muitas chaves foram removidas, pode indicar problema maior
// Mas não vamos recarregar automaticamente para não causar loop infinito
if (cleanedCount > 5) {
	console.warn(`⚠️ Muitas chaves corrompidas detectadas (${cleanedCount}). O sistema pode ter problemas.`);
}

// ✅ Handler global de erros não capturados
window.addEventListener('error', (event) => {
	console.error("🚨 Erro global não capturado:", event.error);
	
	// Se o erro estiver relacionado a localStorage ou JSON, tentar limpar
	if (event.error?.message?.includes("JSON") || 
	    event.error?.message?.includes("localStorage") ||
	    event.error?.message?.includes("Unexpected token")) {
		console.warn("⚠️ Erro relacionado a dados corrompidos detectado. Limpando...");
		clearCorruptedLocalStorage();
	}
});

// ✅ Handler para promessas rejeitadas não tratadas
window.addEventListener('unhandledrejection', (event) => {
	const reasonMessage = String(event.reason?.message || event.reason || "");

	// Erros de rede intermitentes são tratados nos pontos de chamada.
	// Evita tempestade de logs no console em cenários de instabilidade.
	if (
		reasonMessage.includes("Network Error") ||
		reasonMessage.includes("Failed to fetch") ||
		reasonMessage.includes("ERR_FAILED")
	) {
		event.preventDefault();
		return;
	}

	console.error("🚨 Promise rejeitada não tratada:", event.reason);
	
	// Se o erro estiver relacionado a localStorage ou JSON, tentar limpar
	if (event.reason?.message?.includes("JSON") || 
	    event.reason?.message?.includes("localStorage") ||
	    event.reason?.message?.includes("Unexpected token")) {
		console.warn("⚠️ Erro relacionado a dados corrompidos detectado. Limpando...");
		clearCorruptedLocalStorage();
	}
});

// ✅ Contador para evitar loops infinitos
const getReloadCount = () => {
	try {
		const count = parseInt(sessionStorage.getItem('_reloadCount') || '0', 10);
		return count;
	} catch (e) {
		return 0;
	}
};

const incrementReloadCount = () => {
	try {
		const count = getReloadCount();
		sessionStorage.setItem('_reloadCount', (count + 1).toString());
		return count + 1;
	} catch (e) {
		return 1;
	}
};

const resetReloadCount = () => {
	try {
		sessionStorage.removeItem('_reloadCount');
	} catch (e) {
		// Ignorar
	}
};

// ✅ Função para limpar cache e recarregar (MELHORADA)
const clearCacheAndReload = () => {
	const reloadCount = incrementReloadCount();
	console.warn("⚠️ Limpando cache e recarregando página... (tentativa " + reloadCount + ")");
	
	// Se já tentou 2 vezes, parar
	if (reloadCount >= 3) {
		console.error('🚨 Múltiplas tentativas falharam. Parando loop de recarregamento.');
		resetReloadCount();
		return; // Não recarregar mais
	}
	
	// Limpar localStorage problemático
	try {
		clearCorruptedLocalStorage();
	} catch (e) {
		console.error('Erro ao limpar localStorage:', e);
	}
	
	// Desregistrar service workers PRIMEIRO (mais importante)
	if ('serviceWorker' in navigator) {
		navigator.serviceWorker.getRegistrations().then(registrations => {
			return Promise.all(registrations.map(registration => {
				console.log('Desregistrando service worker:', registration.scope);
				return registration.unregister();
			}));
		}).then(() => {
			console.log('✅ Service workers desregistrados');
		}).catch(err => {
			console.error('Erro ao desregistrar service workers:', err);
		});
	}
	
	// Limpar cache do service worker
	if ('caches' in window) {
		caches.keys().then(cacheNames => {
			console.log(`Encontrados ${cacheNames.length} caches para limpar`);
			return Promise.all(cacheNames.map(cacheName => {
				console.log(`Removendo cache: ${cacheName}`);
				return caches.delete(cacheName);
			}));
		}).then(() => {
			console.log('✅ Todos os caches removidos');
			// Recarregar com bypass de cache usando método mais agressivo
			reloadWithCacheBypass();
		}).catch(err => {
			console.error('Erro ao limpar cache:', err);
			// Recarregar mesmo assim
			reloadWithCacheBypass();
		});
	} else {
		// Se não tem cache API, apenas recarregar
		reloadWithCacheBypass();
	}
};

// ✅ Função para recarregar com bypass de cache (método mais agressivo)
const reloadWithCacheBypass = () => {
	console.log('🔄 Recarregando página com bypass de cache...');
	
	// Método 1: Adicionar timestamp na URL (mais confiável)
	const url = new URL(window.location.href);
	url.searchParams.set('_nocache', Date.now().toString());
	
	// Método 2: Usar location.replace com URL modificada (não adiciona ao histórico)
	setTimeout(() => {
		try {
			window.location.replace(url.toString());
		} catch (e) {
			// Fallback: reload com force
			try {
				window.location.reload(true);
			} catch (e2) {
				// Último recurso: recarregar normalmente
				window.location.reload();
			}
		}
	}, 500); // Reduzir delay para 500ms
};

// ✅ Detectar erros de carregamento de recursos (IMEDIATO)
let errorDetected = false;
window.addEventListener('error', (event) => {
	// Prevenir múltiplas execuções
	if (errorDetected) return;
	
	const errorMsg = event.message || '';
	const isResourceError = errorMsg.includes('ERR_INCOMPLETE_CHUNKED_ENCODING') || 
	                       errorMsg.includes('Failed to load resource') ||
	                       errorMsg.includes('net::ERR_') ||
	                       errorMsg.includes('ChunkLoadError');
	
	if (isResourceError && event.target) {
		const resourceUrl = event.target.src || event.target.href || '';
		const isCritical = resourceUrl.includes('/static/js/main.') || 
		                  resourceUrl.includes('/static/js/bundle.') ||
		                  resourceUrl.includes('/static/css/main.');
		
		if (isCritical) {
			const reloadCount = getReloadCount();
			
			// Se já tentou várias vezes, não tentar mais
			if (reloadCount >= 2) {
				console.error('🚨 Múltiplas tentativas falharam. O problema pode ser do servidor.');
				errorDetected = true;
				return false;
			}
			
			errorDetected = true;
			console.error('🚨 ERRO CRÍTICO ao carregar recurso:', resourceUrl);
			console.error('Erro:', errorMsg);
			console.warn('⚠️ Limpando cache IMEDIATAMENTE...');
			
			// Tentar recuperar automaticamente IMEDIATAMENTE
			clearCacheAndReload();
			event.preventDefault(); // Prevenir comportamento padrão
			return false;
		}
	}
}, true); // Capture phase - pegar antes de qualquer tratamento

// ✅ Timeout de segurança: se a aplicação não carregar em 12 segundos, tentar recuperar
let appLoaded = false;
const safetyTimeout = setTimeout(() => {
	if (!appLoaded && !errorDetected) {
		console.warn("⚠️ Aplicação não carregou em 12 segundos. Verificando problemas...");
		
		// Diagnóstico completo
		console.log("📊 DIAGNÓSTICO COMPLETO:");
		console.log("- React disponível:", typeof React !== 'undefined');
		console.log("- ReactDOM disponível:", typeof ReactDOM !== 'undefined');
		console.log("- window.React:", typeof window.React);
		console.log("- window.ReactDOM:", typeof window.ReactDOM);
		console.log("- Online:", navigator.onLine);
		console.log("- User Agent:", navigator.userAgent);
		
		// Verificar se há erro no console ou se o React não renderizou
		const root = document.getElementById("root");
		if (root && root.children.length === 0) {
			console.warn("⚠️ React não renderizou. Verificando scripts...");
			
			// Verificar se os scripts principais foram carregados
			const scripts = Array.from(document.querySelectorAll('script[src]'));
			const mainScript = scripts.find(s => 
				s.src.includes('/static/js/main.') || 
				s.src.includes('/static/js/bundle.')
			);
			
			if (!mainScript || !mainScript.src) {
				console.error("🚨 Script principal não encontrado!");
				console.error("Isso indica que o HTML não está sendo gerado corretamente pelo servidor.");
				errorDetected = true;
				clearCacheAndReload();
				return;
			}
			
			console.log("✅ Script principal encontrado:", mainScript.src);
			console.log("- Script carregado:", mainScript.readyState);
			console.log("- Script tem erro:", mainScript.hasAttribute('data-error') || !!mainScript.onerror);
			
			// Verificar se o script foi carregado (não tem erro)
			const scriptError = scripts.some(s => {
				const src = s.src || '';
				return (src.includes('/static/js/main.') || src.includes('/static/js/bundle.')) &&
				       (s.hasAttribute('data-error') || s.onerror);
			});
			
			if (scriptError) {
				console.error("🚨 Erro detectado no script principal!");
				console.error("O arquivo JS não foi baixado corretamente.");
				errorDetected = true;
				clearCacheAndReload();
				return;
			}
			
			// Verificar se React está disponível mas não renderizou
			if (typeof React !== 'undefined' && typeof ReactDOM !== 'undefined') {
				console.error("🚨 React está disponível mas não renderizou!");
				console.error("Isso indica um erro de execução JavaScript no bundle.");
				console.error("O problema pode ser:");
				console.error("1. Erro JavaScript no código que impede inicialização");
				console.error("2. Dependências faltando");
				console.error("3. Problema de memória no dispositivo");
				console.error("4. Incompatibilidade com navegador antigo");
			} else {
				console.error("🚨 React não está disponível!");
				console.error("O script foi carregado mas React não foi definido.");
				console.error("Isso pode indicar:");
				console.error("1. Erro no bundle que impede React de ser exportado");
				console.error("2. Bundle corrompido");
				console.error("3. Problema de build");
			}
			
			// Se chegou aqui, pode ser outro problema, mas vamos tentar limpar cache
			console.warn("⚠️ Tentando limpar cache e recarregar...");
			errorDetected = true;
			clearCacheAndReload();
		} else if (root && root.children.length > 0) {
			console.log("✅ React renderizou! Root tem", root.children.length, "filhos");
			appLoaded = true;
			clearTimeout(safetyTimeout);
		}
	}
}, 12000);

ReactDOM.render(
	<ErrorBoundary>
		<CssBaseline>
			<App />
		</CssBaseline>
	</ErrorBoundary>,
	document.getElementById("root"),
	() => {
		appLoaded = true;
		clearTimeout(safetyTimeout);
		
		// Resetar contador quando React renderizar com sucesso
		resetReloadCount();
		
		// ✅ Garantir que finishProgress existe antes de chamar
		if (typeof window.finishProgress === 'function') {
			window.finishProgress();
		} else {
			// Se não existir, criar uma função simples
			console.warn("⚠️ window.finishProgress não encontrado. Criando função alternativa...");
			const splashScreen = document.getElementById("splash-screen");
			if (splashScreen) {
				splashScreen.classList.add('fade-out');
				setTimeout(() => {
					splashScreen.remove();
				}, 1000);
			}
		}
	}
);

serviceworker.register()